/**
 * Submit the website forms and handle the errors the API returns.
 *
 * @returns {Object} Containing the form submission and error handling methods.
 */
export const useForm = () => {
	const { locale } = useI18n();
	const { $executeRecaptcha } = useNuxtApp();
	const config = useRuntimeConfig();

	/**
	 * Post the form data to the API.
	 *
	 * @param {FormData} formData The data to submit
	 * @param {String} formName The name of the API endpoint
	 * @param {Object} extraQueryData Optional extra query parameters
	 *
	 * @returns {Promise<Object>} The API response.
	 */
	const submitForm = async (formData, formName, extraQueryData = null) => {
		try {
			formData.append('language', locale.value);

			const token = await $executeRecaptcha();
			if (token) {
				formData.append('g-recaptcha-response', token);
			}

			const result = await $fetch(config.public.apiBaseURL + '/' + formName, {
				method: 'POST',
				body: formData,
				query: { language: locale.value, ...extraQueryData },
			});

			if (result.status === 'error') {
				return { status: 'error', errors: result.errors };
			}

			return { status: 'success', redirect: result.redirect };
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Form submission error:', error);

			return {
				status: 'error',
				errors: [{ message: 'formGeneralError', translationKey: 'formGeneralError', field: null }],
			};
		}
	};

	/**
	 * Convert the form fields into the FormData to submit.
	 *
	 * @param {Object} formRef The reactive list of form fields
	 *
	 * @returns {FormData} The data to submit.
	 */
	const prepareSubmitData = (formRef) => {
		const formData = new FormData();
		formRef.value.forEach((item) => {
			const current = { ...item };
			if (isRef(current.value)) {
				current.value = current.value.value;
			}

			if (Array.isArray(current.value)) {
				current.value.forEach((value) => {
					formData.append(`${current.name}[]`, value);
				});

				return;
			}

			formData.append(current.name, current.value ?? '');
		});

		return formData;
	};

	/**
	 * Set the errors the API returned on the fields they belong to, and keep
	 * the ones without a field as general errors.
	 *
	 * @param {Object} state The form state
	 * @param {Object} response The API response
	 * @param {Object[]} form The form fields
	 *
	 * @returns {void} Nothing.
	 */
	const setErrors = (state, response, form) => {
		if (!response.errors || !response.errors.length) {
			return;
		}

		response.errors.forEach((error) => {
			const field = form.find((item) => item.name === error.field);
			if (field) {
				field.error = error.translationKey || error.message;
			} else {
				state.value.errors.push(error.translationKey || error.message);
			}
		});
	};

	/**
	 * Clear the general errors and the errors on the fields.
	 *
	 * @param {Object} state The form state
	 * @param {Object} formRef The reactive list of form fields
	 *
	 * @returns {void} Nothing.
	 */
	const clearErrors = (state, formRef) => {
		state.value.errors = [];

		formRef.value.forEach((field) => {
			if (field.error) {
				field.error = '';
			}
		});
	};

	/**
	 * Empty the form fields and clear the errors.
	 *
	 * @param {Object} formRef The reactive list of form fields
	 * @param {Object} contactform The form element
	 * @param {Object} state The form state
	 *
	 * @returns {void} Nothing.
	 */
	const resetForm = (formRef, contactform, state) => {
		formRef.value.forEach((field) => {
			field.value = null;
		});
		clearErrors(state, formRef);
		contactform.value?.reset();
	};

	return {
		submitForm,
		prepareSubmitData,
		setErrors,
		clearErrors,
		resetForm,
	};
};
